import { css } from "@linaria/core";
import { useEffect, Suspense, useCallback, useRef, memo } from "react";
import { TransitionGroup } from "react-transition-group";

import {
  isDescriptorInsideCourse,
  useCourseRevisionState,
} from "@pm2/course-revisions";
import { pm2Boundaries, pm2Transitions } from "@pm2/design-tokens";
import { useIsServerSide } from "@pm2/react-utils";
import {
  autoOpenMegaMenuOnMount,
  findPageDescriptorByPath,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import { pm2ConfigStore } from "@pm2/store";
import { asideTokens } from "@pm2/ui-layout/selectors";
import { useLocation } from "@provider/navigation";
import { useUserIdentityState } from "@provider/user-identity";
import { breakpoints } from "@ui/css";
import { combineTokens } from "@utils/css";
import {
  CSSRefTransition,
  NoSSR,
  useIsomorphicLayoutEffect,
  useMatchBreakpoint,
} from "@utils/react";

import { useBrowserLocation } from "../core/provider.navigation";
import { useScrollAnchorIntoView } from "../hooks/scroll-anchor-into-view";

import { Boundary } from "./atom.boundary";
import { ErrorBoundary } from "./atom.error-boundary";
import { Head } from "./atom.head";
import { PageResolver } from "./organism.resolver";

// How long to wait before the should be auto-expanded after a page is fully
// rendered. This is only applicable in case a page needs to menu auto-expanded.
const OPEN_MEGAMENU_MS_AFTER_READY = 1000;

export type PageRouterProps = {
  /**
   * triggered when navigation to a new page begins (ie. before it can be
   * reliably rendered).
   */
  onNavigationStart?(path: string): void;

  /**
   * triggered when a new page has been fetched and is being rendered.
   */
  onNavigationEnd?(path: string): void;
};

/**
 * handle routing with useLocation from react-dom-router, to fetch and display
 * content as the user browses a portal.
 *
 * using Suspense to pre-fetch content required to render a page, prior to
 * actually displaying it (this retains previous page within view, until it
 * is ready to display).
 */
function PageRouterUnwrapped({
  onNavigationStart,
  onNavigationEnd,
}: PageRouterProps): React.JSX.Element {
  const serverSide = useIsServerSide();

  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const sitemap = pm2SitemapStore.useSelector((state) => state);

  const hasResolvedUser = useUserIdentityState(
    (state) => state.user !== undefined,
  );
  const location = useLocation();
  const browserLocation = useBrowserLocation();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const prevPath = useRef<string | undefined>(undefined);
  const isEditingRevision = useCourseRevisionState((state) => state.isEditing);
  const currentDescriptor = findPageDescriptorByPath(
    sitemap,
    location.pathname,
  );

  useScrollAnchorIntoView();

  // We do not want hash changes to trigger re-renders. As such we extact our
  // paths without hashes and use these as keys in our router.
  const browserPathWithoutHash = `${browserLocation.pathname}${browserLocation.search}`;
  const locationPathWithoutHash = `${location.pathname}${location.search}`;

  // trigger onNavigationStart callback whenever we detect a change in browser
  // history
  useIsomorphicLayoutEffect(() => {
    if (
      onNavigationStart !== undefined &&
      browserPathWithoutHash !== prevPath.current
    ) {
      onNavigationStart(browserPathWithoutHash);
      prevPath.current = browserPathWithoutHash;
    } else {
      // Even though we haven't changed pages, then the hash may have
      // changed. As such we flush our browserLocation() to remain in sync.
      browserLocation.flush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onNavigationStart, browserLocation.asPath, location.asPath]);

  const handleLoad = useCallback(
    (nextPath: string): void => {
      if (browserPathWithoutHash !== nextPath) {
        // ignore load event if it's triggered by a page that the user left
        // afterwards
        return;
      }

      if (onNavigationEnd !== undefined) {
        onNavigationEnd(nextPath);
      }

      browserLocation.flush();

      if (!sitemap.hasRenderedFirstPage) {
        sitemapStore.dispatch(sitemapStore.actions.registerPageRender());

        // Once the initial mounting has finished, then auto-expand the
        // megamenu if we're on a page that is part of a mega-menu.
        setTimeout(() => {
          autoOpenMegaMenuOnMount(
            configStore,
            sitemapStore,
            nextPath,
            isMobile ?? false,
          );
        }, OPEN_MEGAMENU_MS_AFTER_READY);
      }
    },

    [
      browserPathWithoutHash,
      onNavigationEnd,
      browserLocation,
      sitemap,
      configStore,
      sitemapStore,
      isMobile,
    ],
  );

  // show custom fallback while fetching new pages (ie. whenever browser
  // location doesn't match the currently rendered path)
  const showInitialThrobber =
    serverSide || !sitemap.hasRenderedFirstPage || !hasResolvedUser;
  const loadNextPage =
    !serverSide &&
    (!sitemap.hasRenderedFirstPage ||
      browserPathWithoutHash !== locationPathWithoutHash);

  useInitialThrobber(showInitialThrobber);

  return (
    <ErrorBoundary currentPath={browserPathWithoutHash}>
      <Boundary>
        <Head path={location.pathname} />

        <TransitionGroup className={transitionWrapperCls}>
          {!showInitialThrobber && (
            <CSSRefTransition
              key={`${location.pathname}-${location.searchParams.get(
                "resource",
              )}-${showInitialThrobber}-${
                isEditingRevision &&
                isDescriptorInsideCourse(sitemap, currentDescriptor)
              }`}
              classNames={fadeTransitionCls}
              timeout={{
                enter: TRANSITION_DURATION_MS + TRANSITION_DELAY_MS,
                exit: showInitialThrobber
                  ? TRANSITION_DURATION_MS + TRANSITION_DELAY_MS
                  : TRANSITION_DELAY_MS - 50,
              }}
            >
              {(ref) => (
                <div ref={ref} className={pageWrapperCls}>
                  <Suspense fallback={null}>
                    <PageResolver
                      asPath={locationPathWithoutHash}
                      pathname={location.pathname}
                      searchParams={location.searchParams}
                    />
                  </Suspense>
                </div>
              )}
            </CSSRefTransition>
          )}
        </TransitionGroup>

        {/**
         * on the server side layer we need to mount the PageResolver to be
         * able to return 404 Not Found on urls that doesn't map to anything
         */}
        {serverSide && (
          <PageResolver
            serverSide={serverSide}
            asPath={locationPathWithoutHash}
            pathname={location.pathname}
            searchParams={location.searchParams}
          />
        )}

        {/**
         * content was actually rendered above; we use this pattern because we
         * cannot currently apply transitions correctly with Suspense and it is
         * incompatible with server side rendering
         *
         * we do however want to use the core concepts of Suspense to keep the
         * application built on a well-established React architecture
         */}
        {loadNextPage && (
          <NoSSR key={`suspense-${browserPathWithoutHash}`}>
            <Suspense fallback={null}>
              <PageResolver
                asPath={browserPathWithoutHash}
                pathname={browserLocation.pathname}
                searchParams={browserLocation.searchParams}
                onLoad={handleLoad}
              />
            </Suspense>
          </NoSSR>
        )}
      </Boundary>
    </ErrorBoundary>
  );
}

export const PageRouter = memo(PageRouterUnwrapped);

/**
 * Manages the native initial throbber, ensuring it is removed once the page
 * has been loaded.
 */
function useInitialThrobber(showInitialThrobber: boolean) {
  useEffect(() => {
    // Ensure we remove the throbber once it is not needed anymore
    if (!showInitialThrobber) {
      const throbber = document.querySelector(
        "#throbber",
      ) as HTMLElement | null;

      throbber?.classList.add("exit");
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      throbber?.offsetWidth;
      throbber?.classList.add("exit-active");

      setTimeout(() => {
        throbber?.parentElement?.remove();
      }, TRANSITION_DURATION_MS);
    }
  }, [showInitialThrobber]);
}

const TRANSITION_DURATION_MS = 200;
const TRANSITION_DELAY_MS = 100;

export const pageTokens = combineTokens("--page", {
  asideLeftTransformation: "0px",
  asideRightTransformation: "0px",
});

const transitionWrapperCls = css`
  position: relative;

  display: flex;
  flex-grow: 1;
`;

const fadeTransitionCls = css`
  &-enter {
    z-index: 2;

    opacity: 0.001;
    transition: none !important;

    &-active {
      opacity: 1;
      transition: opacity ${TRANSITION_DURATION_MS}ms linear
        ${TRANSITION_DELAY_MS}ms !important;
    }
  }

  &-exit {
    position: absolute;
    inset: 0;

    display: none !important;
  }
`;

const pageWrapperCls = css`
  width: 100%;

  display: flex;
  flex-direction: column;
  flex-grow: 1;

  transition: ${pm2Transitions.asideCollapse()};

  /**
   * The following transformations attempts to ensure that content isn't
   * overlapped by asides while 1) not shrinking content and 2) still
   * respecting page boundaries
   */
  @media (max-width: 2200px) {
    body.aside--left:not(.aside--left--prevent-overlap) & {
      ${pageTokens.asideLeftTransformation
        .__override(
          `min(
            (
              ${pm2Boundaries.container.offsetLeft()} -
                ${pm2Boundaries.root.paddingLeft()}
            ),
            (${asideTokens.leftAsideWidth()} / 2)
          )`,
        )
        .toString()};

      transform: translateX(${pageTokens.asideLeftTransformation()});
    }

    body.aside--right:not(.aside--right--prevent-overlap) & {
      ${pageTokens.asideRightTransformation
        .__override(
          `calc(-1 * min(
            (
              ${pm2Boundaries.container.offsetRight()} -
                ${pm2Boundaries.root.paddingRight()}
            ),
            (${asideTokens.rightAsideWidth()} / 2)
          ))`,
        )
        .toString()};

      transform: translateX(${pageTokens.asideRightTransformation()});
    }
  }
`;
